import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateBusinessMutation } from '../../../api/businesses';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { useFormError } from '../../../hooks/useFormError';
import { Box, Button, ContentIsland, FormTextField, HookForm, Stack } from '@razz-team/ui-web';
import { AuthLayout } from '../AuthPage/AuthLayout';
import { Route } from '../../../routes/new-business';

type NewBusinessFormValues = {
  legalName: string;
};

export function NewBusinessPage() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();

  const idempotencyKey = useIdempotencyKey();
  const createMutation = useCreateBusinessMutation({ idempotencyKey });
  const handleSubmit = useCallback(
    (values: NewBusinessFormValues) => {
      createMutation.mutate(values);
    },
    [createMutation],
  );

  useEffect(() => {
    if (createMutation.isSuccess) {
      navigate({ to: '/account/home', replace: true });
    }
  }, [createMutation.isSuccess, navigate]);

  const form = useForm<NewBusinessFormValues>();
  useFormError(form, 'legalName', createMutation.error);

  return (
    <AuthLayout>
      <Box
        width="100%"
        height="100%"
        display="flex"
        alignSelf="center"
        justifySelf="center"
        alignItems="center"
        justifyContent="center"
        component={HookForm}
        form={form}
        onSubmit={handleSubmit}
      >
        <Stack spacing={4} padding={4} sx={{ width: '100%', maxWidth: 400 }}>
          <ContentIsland title={t('Open business account')}>
            <Stack spacing={4} padding={4}>
              <FormTextField
                name="legalName"
                label={t('Company name')}
                required
                minLength={3}
                autoFocus
                autoComplete="off"
                fullWidth
              />
            </Stack>
          </ContentIsland>
          <Button
            type="submit"
            loading={createMutation.isPending}
            disabled={!form.formState.isDirty}
            size="large"
            fullWidth
          >
            {t('Continue')}
          </Button>
        </Stack>
      </Box>
    </AuthLayout>
  );
}
